import React from 'react';

import { styled, CHOICE_INDICATOR } from '../../common';
import type { ToggleProps } from './Toggle';
import { animationTransition } from '../../utils';
import type { ThemedStyledProps, DefaultTheme } from 'styled-components';

const getBackgroundColor = ({ theme }: ThemedStyledProps<ToggleProps, DefaultTheme>) => theme.colors.mono14;

export const Indicator = styled.div`
  width: 64px;
  position: relative;
  height: 28px;
  border-radius: 60px;
  background-color: ${props => props.theme.colors.mono4};
  transition: ${animationTransition('background-color')};
  flex-shrink: 0;

  :before {
    background: #fff;
    position: absolute;
    content: ' ';
    display: block;
    border-radius: 12px;
    top: 50%;
    transform: translateY(-50%) translateZ(0.1px) translateX(2px);
    height: 24px;
    width: 40px;
    transition: ${animationTransition('transform')};
  }

  ${props => props.theme.mediaQueries.sm} {
    width: 56px;
    height: 24px;

    &:before {
      border-radius: 10px;
      height: 20px;
      width: 32px;
    }
  }
`;

const Input: React.FC<ToggleProps> = ({ children, alignIndicator, label, defaultChecked, hideLabel, ...props }) => (
  <input
    aria-checked={props.checked}
    onChange={props.onChange}
    {...props}
    defaultChecked={defaultChecked}
    type={'checkbox'}
  />
);

export const StyledInput = styled(Input)`
  /*
   * The input is visually hidden but still focusable, so the control was keyboard-operable with no
   * indicator at all (WCAG 2.4.7). The ring goes on the sibling indicator, which is what the user
   * can actually see.
   *
   * 'outline' with an offset rather than the fields' flush 'box-shadow': the indicator is a
   * *filled* pill that turns inputBorderFocused when checked, so a flush ring in that same colour
   * would merge into the pill and disappear in exactly one of the two states. The offset also keeps
   * the ring off the pill without assuming what colour sits behind the control.
   *
   * See doc/field-focus-consistency/PLAN.md.
   */
  :enabled:focus-visible ~ .${CHOICE_INDICATOR} {
    outline: 2px solid ${props => props.theme.colors.inputBorderFocused};
    outline-offset: 2px;
  }

  &:checked ~ ${Indicator} {
    background-color: ${props => getBackgroundColor(props)};
    &:before {
      transform: translateY(-50%) translateZ(0.1px) translateX(22px);

      ${props => props.theme.mediaQueries.sm} {
        transform: translateY(-50%) translateZ(0.1px) translateX(22px);
      }
    }
  }

  &:disabled ~ ${Indicator} {
    background-color: ${props => props.theme.colors.mono3};
    &:before {
      background: ${props => props.theme.colors.mono1};
    }
  }
`;
